import React, { useCallback, useEffect, useRef, useState } from 'react';

import InvoiceTabs from './InvoiceTabs';
import InvoicesDetail from './InvoicesDetail';
import useInvoice from './useInvoice';
import useInvoicesList from './useInvoicesList';
import { useIsLarge, useIsSmall } from '../../../../Components/Context/Hooks/mediaQueries';
import Box from '../../../../Components/UI/Box';
import { Client } from '../../../../Types/client';
import { Department } from '../../../../Types/department';
import { MATCHED_STATUS } from '../../../../Types/invoice';
import { MENUS } from '../../../../Utils/Menus';
import { EVENTS, track } from '../../../../Utils/analytics';
import { assertErrors } from '../../../../Utils/errors';
import useGlobalContext, { InvoiceContext } from '../../Context';
import { InvoicesHeader } from '../../Header';
import LoadingSkeleton from '../../LoadingSkeleton';
import Main from '../../Main';
import MainBody from '../../Main/MainBody';
import InvoicesNavigation from '../../Navigation/Invoices';
import Translations from '../../Translations';

interface InvoicesProps {
  themeLoading: boolean;
  invoiceId: string;
}

const UNMATCHED = [MATCHED_STATUS.UNMATCHED];
const PAID = [MATCHED_STATUS.FULLY_PAID, MATCHED_STATUS.MIXED];
const CREDITED = [MATCHED_STATUS.FULLY_CREDITED];

export enum TABS {
  INVOICE,
  CREDIT_NOTES,
}

const Invoices: React.FC<InvoicesProps> = ({ themeLoading, invoiceId }) => {
  const language = useRef<string | null>(null);
  const isSmall = useIsSmall();
  const isLarge = useIsLarge();
  const openInvoices = useInvoicesList(UNMATCHED);
  const paidInvoices = useInvoicesList(PAID);
  const creditedInvoices = useInvoicesList(CREDITED);
  const cachedDepartment = useRef<Department>();
  const cachedClient = useRef<Client>();
  const [activeTab, setActiveTab] = useState<TABS>(TABS.INVOICE);
  const [blockedInvoices, setBlockedInvoices] = useState(new Map<string, boolean>());
  const { paymentServiceProvidersEnabled, openMenu, handleOpenMenu, handleCloseMenu } = useGlobalContext();

  const {
    loading,
    invoice,
    error: invoiceError,
    pdfs,
    invoicePdf,
    mutate,
  } = useInvoice(invoiceId, activeTab === TABS.CREDIT_NOTES);
  const listsLoading = !openInvoices.data || !paidInvoices.data || !creditedInvoices.data;

  const mutateOpenInvoices = openInvoices.mutate;
  const mutatePaidInvoices = paidInvoices.mutate;
  const mutateCreditedInvoices = creditedInvoices.mutate;
  const clearCache = useCallback(async () => {
    await Promise.all([mutate(), mutateOpenInvoices(), mutatePaidInvoices(), mutateCreditedInvoices()]);
  }, [mutate, mutateOpenInvoices, mutatePaidInvoices, mutateCreditedInvoices]);

  const handleOpenInvoices = () => {
    setActiveTab(TABS.INVOICE);
    track(EVENTS.CLICKED_INVOICE_TAB);
  };
  const handleOpenCreditNotes = () => {
    setActiveTab(TABS.CREDIT_NOTES);
    track(EVENTS.CLICKED_CREDIT_NOTE_TAB);
  };

  const localOpenMenu = useRef(openMenu);
  localOpenMenu.current = openMenu;

  const lastInvoiceId = useRef(invoiceId);
  useEffect(() => {
    if (lastInvoiceId.current !== invoiceId) {
      setActiveTab(TABS.INVOICE);
      handleCloseMenu!();
      localOpenMenu.current = null;
    }

    lastInvoiceId.current = invoiceId;
  }, [handleCloseMenu, invoiceId]);

  const lastSuggestedInvoiceId = useRef('');
  useEffect(() => {
    if (!invoice || !paymentServiceProvidersEnabled) {
      return;
    }

    if (lastSuggestedInvoiceId.current === invoice.id) {
      return;
    }
    lastSuggestedInvoiceId.current = invoice.id;

    if (invoice.total.due.amount === 0 || blockedInvoices.get(invoice.id)) {
      return;
    }

    if (!!localOpenMenu.current || !isLarge) {
      return;
    }

    handleOpenMenu!(MENUS.PAYMENT);
  }, [blockedInvoices, handleOpenMenu, invoice, isLarge, paymentServiceProvidersEnabled]);

  let error: unknown;
  if (listsLoading) {
    error = assertErrors(openInvoices.error, paidInvoices.error, creditedInvoices.error);
  }

  if (invoice) {
    cachedDepartment.current = invoice.department;
    cachedClient.current = invoice.client;
  }

  if (!loading || invoice) {
    language.current = invoice?.client.language || navigator.language.split('-')[0];
  }

  useEffect(() => {
    if (!invoice) {
      return;
    }

    document.title = `InvoiceCloud - ${invoice.department.name}`;
  }, [invoice]);

  const setMadeTooManyPaymentAttempts = useCallback(
    (madeTooManyPaymentAttempts: boolean) => {
      if (madeTooManyPaymentAttempts === blockedInvoices.get(invoiceId)) {
        return;
      }

      blockedInvoices.set(invoiceId, madeTooManyPaymentAttempts);
      setBlockedInvoices(new Map(blockedInvoices));
    },
    [blockedInvoices, invoiceId],
  );

  return (
    <Translations
      loader={<LoadingSkeleton style={isSmall ? 'mobile' : 'collapsed'} />}
      loading={(themeLoading || listsLoading) && !error}
      language={language.current}
    >
      <InvoiceContext.Provider
        value={{
          invoiceId,
          invoice,
          openInvoices,
          paidInvoices,
          creditedInvoices,
          department: cachedDepartment.current,
          pdfs,
          invoicePdf,
          client: cachedClient.current,
          clearCache,
          madeTooManyPaymentAttempts: blockedInvoices.get(invoiceId) ?? false,
          setMadeTooManyPaymentAttempts,
        }}
      >
        <Main>
          <InvoicesNavigation />
          <Box flex={1} display="flex" flexDirection="column" overflow="auto">
            <InvoicesHeader />
            {!isSmall && (
              <InvoiceTabs
                invoice={invoice}
                fullWidth={false}
                handleOpenInvoices={handleOpenInvoices}
                handleOpenCreditNotes={handleOpenCreditNotes}
                activeTab={activeTab}
              />
            )}
            <MainBody language={language.current} key={invoiceId}>
              <InvoicesDetail
                activeTab={activeTab}
                handleOpenInvoices={handleOpenInvoices}
                handleOpenCreditNotes={handleOpenCreditNotes}
                defaultLanguage={language.current}
                loading={loading}
                error={invoiceError}
              />
            </MainBody>
          </Box>
        </Main>
      </InvoiceContext.Provider>
    </Translations>
  );
};

export default Invoices;
